import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listScans, type AdminScanSummary } from '../lib/api';
import { theme } from '../theme';

export function ScansPage() {
  const [scans, setScans] = useState<AdminScanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [makeFilter, setMakeFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<'' | 'success' | 'failure'>('');
  const [regPlateFilter, setRegPlateFilter] = useState('');
  // Client-side filter on operatorOutcome (the human-judgement override).
  // Distinct from outcomeFilter which is sk-derived (success/failure).
  // Server-side doesn't filter by operatorOutcome today; we filter the
  // returned page client-side to keep this PR self-contained. If the
  // marked-failed count grows large enough that single-page filters miss
  // matches further down the list, add a query param to listScans.
  const [operatorOutcomeFilter, setOperatorOutcomeFilter] = useState<
    '' | 'marked-failed' | 'not-marked'
  >('');

  useEffect(() => {
    setLoading(true);
    listScans({
      limit: 50,
      make: makeFilter || undefined,
      outcome: outcomeFilter || undefined,
      regPlate: regPlateFilter || undefined,
    })
      .then((res) => {
        setScans(res.scans);
        setNextCursor(res.nextCursor);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [makeFilter, outcomeFilter, regPlateFilter]);

  const loadMore = async () => {
    if (!nextCursor) return;
    const res = await listScans({
      limit: 50,
      cursor: nextCursor,
      make: makeFilter || undefined,
      outcome: outcomeFilter || undefined,
      regPlate: regPlateFilter || undefined,
    });
    setScans((prev) => [...prev, ...res.scans]);
    setNextCursor(res.nextCursor);
  };

  return (
    <div>
      <h1>Recent Scans</h1>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input
          placeholder="Filter by make…"
          value={makeFilter}
          onChange={(e) => setMakeFilter(e.target.value)}
        />
        <input
          placeholder="Filter by reg plate…"
          value={regPlateFilter}
          onChange={(e) => setRegPlateFilter(e.target.value)}
          style={{ fontFamily: theme.fonts.mono }}
        />
        <select
          value={outcomeFilter}
          onChange={(e) => setOutcomeFilter(e.target.value as '' | 'success' | 'failure')}
        >
          <option value="">All outcomes</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </select>
        <select
          value={operatorOutcomeFilter}
          onChange={(e) =>
            setOperatorOutcomeFilter(e.target.value as '' | 'marked-failed' | 'not-marked')
          }
          title="Filter by staff override (operatorOutcome)"
        >
          <option value="">All operator overrides</option>
          <option value="marked-failed">Marked failed by staff</option>
          <option value="not-marked">Not marked failed</option>
        </select>
      </div>
      {error && <p style={{ color: theme.status.error }}>Error: {error}</p>}
      {loading && <p>Loading…</p>}
      {!loading && scans.length === 0 && <p>No scans match this filter.</p>}
      {!loading && scans.length > 0 && (
        <>
          <ScanTable
            scans={scans.filter((s) => {
              if (operatorOutcomeFilter === 'marked-failed') return s.operatorOutcome === 'failed';
              if (operatorOutcomeFilter === 'not-marked') return s.operatorOutcome !== 'failed';
              return true;
            })}
          />
          {nextCursor && (
            <button
              onClick={loadMore}
              style={{
                marginTop: 16,
                padding: '8px 16px',
                background: theme.surface.panel,
                border: `1px solid ${theme.surface.borderStrong}`,
                color: theme.text.primary,
                borderRadius: theme.radius.md,
              }}
            >
              Load more
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ScanTable({ scans }: { scans: AdminScanSummary[] }) {
  return (
    <table style={{ width: '100%' }}>
      <thead>
        <tr style={{ background: theme.surface.header }}>
          <Th>Scanned</Th>
          <Th>ID</Th>
          <Th>Dealer</Th>
          <Th>Vehicle</Th>
          <Th>Reg plate</Th>
          <Th>VIN</Th>
          <Th>Outcome</Th>
          <Th>Status</Th>
          <Th>DTCs</Th>
          <Th>Readings</Th>
          <Th>Actions</Th>
        </tr>
      </thead>
      <tbody>
        {scans.map((s) => (
          <tr key={s.scanId} style={{ borderBottom: `1px solid ${theme.surface.border}` }}>
            <Td>
              <time>{formatRelative(s.scannedAt)}</time>
            </Td>
            <Td style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>
              {s.scanShortcodeBody ? `OB-${s.scanShortcodeBody}` : `${s.scanId.slice(0, 8)}…`}
            </Td>
            <Td>
              <DealerCell summary={s} />
            </Td>
            <Td>
              {s.make ?? '—'} {s.model ?? ''} {s.modelYear ?? ''}
            </Td>
            <Td style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>
              {s.registrationPlate ?? '—'}
            </Td>
            <Td style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>{s.vin ?? '—'}</Td>
            <Td>
              <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                <Badge variant={s.outcome === 'success' ? 'ok' : 'fail'}>{s.outcome}</Badge>
                {s.operatorOutcome === 'failed' && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      color: theme.status.error,
                      background: theme.surface.panel,
                      border: `1px solid ${theme.status.error}`,
                      padding: '1px 6px',
                      borderRadius: theme.radius.sm,
                      whiteSpace: 'nowrap',
                    }}
                    title="Staff marked this scan as failed via the dashboard"
                  >
                    MARKED FAILED
                  </span>
                )}
              </span>
            </Td>
            <Td>{s.overallStatus ?? '—'}</Td>
            <Td>{s.totalDtcs ?? '—'}</Td>
            <Td style={{ fontFamily: theme.fonts.mono, fontSize: 12 }}>
              <ReadingsCount scan={s} />
            </Td>
            <Td>
              <Link to={`/scans/${s.scanId}`}>Details</Link>
              {s.reportUrl && (
                <>
                  {' · '}
                  <a href={s.reportUrl} target="_blank" rel="noreferrer">
                    PDF
                  </a>
                </>
              )}
              {s.batteryReportUrl && (
                <>
                  {' · '}
                  <a href={s.batteryReportUrl} target="_blank" rel="noreferrer">
                    Battery PDF
                  </a>
                </>
              )}
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReadingsCount({ scan }: { scan: AdminScanSummary }) {
  // Sum BOTH reading layers — the generic Mode 01 live-sensor sweep and the
  // OBDb manufacturer-specific signal-set decodings. Previous incarnation
  // ('ReadingsRatio') showed signalSetStats.readingsCount / totalCommands
  // which was OBDb-only — that meant a Nissan Juke scan with 25 Mode 01
  // sensors but 0 OBDb readings displayed "0/20", and a Toyota Yaris with
  // 24 Mode 01 sensors but no OBDb match displayed "0/0". The dealer
  // doesn't care which layer captured a reading; they care about the total
  // count of useful sensor data. liveSensorsCount is denormalised by ingest
  // alongside signalSetStats; null on records ingested pre-this-PR so old
  // records fall back to showing just the OBDb count (their prior display).
  const liveCount = scan.liveSensorsCount ?? 0;
  const obdbCount = scan.signalSetStats?.readingsCount ?? 0;
  const total = liveCount + obdbCount;
  if (total === 0) return <span style={{ color: theme.text.tertiary }}>—</span>;
  return <span>{total}</span>;
}

function DealerCell({ summary }: { summary: AdminScanSummary }) {
  if (!summary.userId) {
    return <span style={{ color: theme.text.tertiary }}>—</span>;
  }
  const name = [summary.firstName, summary.lastName].filter(Boolean).join(' ').trim();
  return (
    <Link to={`/app-users/${encodeURIComponent(summary.userId)}`}>{name || 'View dealer'}</Link>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        color: theme.text.secondary,
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '8px 12px', fontSize: 13, ...style }}>{children}</td>;
}
function Badge({ children, variant }: { children: React.ReactNode; variant: 'ok' | 'fail' }) {
  const color = variant === 'ok' ? theme.status.success : theme.status.error;
  return <span style={{ color, fontWeight: 600 }}>{children}</span>;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}
